import { Badge } from '../components/Badge'
import { Card } from '../components/Card'
import { CopyButton } from '../components/CopyButton'
import { useDb } from '../state/DbProvider'
import {
  agenciesForSuperAgency,
  connectorsForAgency,
  connectorsVisibleToPartner,
  findUser,
  getCurrentUser,
  getSuperAgencyForAgency,
  isAgencyLikeRole,
  sumAgencyRewards,
  sumConnectorRewards,
  sumConnectorSales,
  sumPartnerManagedSales,
} from '../state/selectors'
import { buildAgencyInviteLink, buildSuperAgencyAgencyInviteLink } from '../utils/affiliate'
import { formatJPY, formatPercent } from '../utils/format'
import {
  getEffectiveAgencyRate,
  getEffectiveConnectorRate,
  getEffectiveSuperAgencyRate,
} from '../utils/reward'

export default function NetworkPage() {
  const { db } = useDb()
  if (!db) return null

  const me = getCurrentUser(db)
  const inviter = me.role === 'コネクター' ? findUser(db, me.connector?.introducedById) : null
  const parentAgency = me.role === 'コネクター' ? findUser(db, me.connector?.agencyId) ?? null : null
  const parentSuperAgency = me.role === '代理店' ? getSuperAgencyForAgency(db, me.id) ?? null : null
  const connectorInviteCode = isAgencyLikeRole(me.role) ? me.agency?.inviteCode ?? '' : ''
  const connectorInviteLink = connectorInviteCode ? buildAgencyInviteLink(connectorInviteCode) : ''
  const agencyInviteLink =
    me.role === '総代理店' && connectorInviteCode
      ? buildSuperAgencyAgencyInviteLink(connectorInviteCode)
      : ''

  return (
    <div className="grid gap-4">
      <Card title="チーム / 紹介ネットワーク" subtitle="所属、招待リンク、チーム実績を確認できます。">
        <div className="grid gap-2 px-5 py-4 text-sm text-slate-700">
          <div>
            現在のロール: <span className="font-semibold">{me.role}</span>
          </div>
          {me.role === '総代理店' ? (
            <div>
              現在の総代理店率:{' '}
              <span className="font-semibold">{formatPercent(getEffectiveSuperAgencyRate(db.settings))}</span>
            </div>
          ) : me.role === '代理店' ? (
            <div>
              現在の代理店総率:{' '}
              <span className="font-semibold">{formatPercent(getEffectiveAgencyRate(db.settings, me))}</span>
            </div>
          ) : me.role === 'コネクター' ? (
            <div>
              現在のコネクター率:{' '}
              <span className="font-semibold">{formatPercent(getEffectiveConnectorRate(db.settings, me))}</span>
            </div>
          ) : null}
        </div>
      </Card>

      {me.role === 'コネクター' ? (
        <Card title="所属情報" subtitle="コネクターの所属先と紹介元です。">
          <div className="grid gap-3 px-5 py-4 text-sm">
            <div>
              所属先: <span className="font-semibold">{parentAgency?.name ?? '未設定'}</span>
            </div>
            <div>
              所属ロール: <span className="font-semibold">{parentAgency?.role ?? '未設定'}</span>
            </div>
            <div>
              紹介元: <span className="font-semibold">{inviter?.name ?? 'なし'}</span>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="grid gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">売上累計</span>
                  <span className="font-semibold">{formatJPY(sumConnectorSales(db, me.id))}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">報酬累計</span>
                  <span className="font-semibold">{formatJPY(sumConnectorRewards(db, me.id))}</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      ) : null}

      {me.role === '代理店' ? (
        <>
          {parentSuperAgency ? (
            <Card title="所属総代理店" subtitle="この代理店を担当する総代理店です。">
              <div className="grid gap-2 px-5 py-4 text-sm text-slate-700">
                <div className="font-semibold text-slate-900">{parentSuperAgency.name}</div>
                <div>{parentSuperAgency.email}</div>
              </div>
            </Card>
          ) : null}

          <AgencyTeamCard
            agencyId={me.id}
            title="自社チーム"
            subtitle="代理店と配下コネクターの実績一覧です。"
          />
        </>
      ) : null}

      {me.role === '総代理店' ? (
        <SuperAgencyNetworkCard superAgencyId={me.id} />
      ) : null}

      {isAgencyLikeRole(me.role) ? (
        <Card
          title="招待リンク"
          subtitle={me.role === '総代理店' ? 'コネクター招待と代理店申請招待の両方を管理できます。' : 'コネクターの招待に使うリンクです。'}
        >
          <div className="grid gap-4 px-5 py-4">
            <div className="grid gap-3">
              <div className="text-sm font-semibold text-slate-900">コネクター招待リンク</div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-slate-600">招待コード</span>
                <span className="rounded bg-slate-100 px-2 py-1 font-mono text-xs text-slate-900">
                  {connectorInviteCode || '-'}
                </span>
                <CopyButton value={connectorInviteCode} label="コードをコピー" copiedLabel="コピー済み" />
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  readOnly
                  value={connectorInviteLink}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs"
                />
                <CopyButton
                  value={connectorInviteLink}
                  label="リンクをコピー"
                  copiedLabel="コピー済み"
                  className="shrink-0"
                />
              </div>
            </div>

            {me.role === '総代理店' ? (
              <div className="grid gap-3 border-t border-slate-200 pt-4">
                <div className="text-sm font-semibold text-slate-900">代理店申請招待リンク</div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    readOnly
                    value={agencyInviteLink}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs"
                  />
                  <CopyButton
                    value={agencyInviteLink}
                    label="リンクをコピー"
                    copiedLabel="コピー済み"
                    className="shrink-0"
                  />
                </div>
                <div className="text-xs text-slate-600">
                  このリンクからの代理店申請は、所属総代理店が自動で固定されます。
                </div>
              </div>
            ) : null}
          </div>
        </Card>
      ) : null}
    </div>
  )
}

function AgencyTeamCard(props: { agencyId: string; title: string; subtitle: string }) {
  const { db } = useDb()
  if (!db) return null

  const agency = findUser(db, props.agencyId)
  const team = connectorsForAgency(db, props.agencyId)
  if (!agency) return null

  return (
    <Card title={props.title} subtitle={props.subtitle}>
      <div className="grid gap-4 px-5 py-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-base font-semibold text-slate-900">{agency.name}</div>
              <div className="text-xs text-slate-500">{agency.email}</div>
            </div>
            <Badge tone="blue">コネクター {team.length}人</Badge>
          </div>

          <div className="mt-3 grid gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">代理店総率</span>
              <span className="font-semibold">{formatPercent(getEffectiveAgencyRate(db.settings, agency))}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">チーム売上</span>
              <span className="font-semibold">{formatJPY(sumPartnerManagedSales(db, agency.id))}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">代理店報酬累計</span>
              <span className="font-semibold">{formatJPY(sumAgencyRewards(db, agency.id))}</span>
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {team.map((connector) => {
              const intro = findUser(db, connector.connector?.introducedById)
              return (
                <div key={connector.id} className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="font-semibold text-slate-900">{connector.name}</div>
                  <div className="mt-1 text-xs text-slate-500">{connector.email}</div>
                  <div className="mt-3 grid gap-1 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">報酬率</span>
                      <span className="font-medium">{formatPercent(getEffectiveConnectorRate(db.settings, connector))}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">売上累計</span>
                      <span className="font-medium">{formatJPY(sumConnectorSales(db, connector.id))}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">報酬累計</span>
                      <span className="font-medium">{formatJPY(sumConnectorRewards(db, connector.id))}</span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">紹介元: {intro?.name ?? 'なし'}</div>
                  </div>
                </div>
              )
            })}
            {team.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                コネクターはまだ招待されていません。
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Card>
  )
}

function SuperAgencyNetworkCard(props: { superAgencyId: string }) {
  const { db } = useDb()
  if (!db) return null

  const superAgency = findUser(db, props.superAgencyId)
  if (!superAgency) return null

  const subordinateAgencies = agenciesForSuperAgency(db, props.superAgencyId)
  const directConnectors = connectorsForAgency(db, props.superAgencyId)
  const managedConnectors = connectorsVisibleToPartner(db, props.superAgencyId)

  return (
    <Card title="総代理店ネットワーク" subtitle="直属コネクターと配下代理店の全体像です。">
      <div className="grid gap-4 px-5 py-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-base font-semibold text-slate-900">{superAgency.name}</div>
              <div className="text-xs text-slate-500">{superAgency.email}</div>
            </div>
            <Badge tone="blue">配下代理店 {subordinateAgencies.length}社</Badge>
          </div>

          <div className="mt-3 grid gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">総代理店率</span>
              <span className="font-semibold">{formatPercent(getEffectiveSuperAgencyRate(db.settings))}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">直属コネクター数</span>
              <span className="font-semibold">{directConnectors.length}人</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">管理対象コネクター数</span>
              <span className="font-semibold">{managedConnectors.length}人</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">管理売上</span>
              <span className="font-semibold">{formatJPY(sumPartnerManagedSales(db, superAgency.id))}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">総代理店報酬累計</span>
              <span className="font-semibold">{formatJPY(sumAgencyRewards(db, superAgency.id))}</span>
            </div>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-sm font-semibold text-slate-900">直属コネクター</div>
            <div className="mt-3 grid gap-2 text-sm">
              {directConnectors.map((connector) => (
                <div key={connector.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="font-medium text-slate-900">{connector.name}</div>
                  <div className="text-xs text-slate-500">{connector.email}</div>
                </div>
              ))}
              {directConnectors.length === 0 ? <div className="text-sm text-slate-500">直属コネクターはいません。</div> : null}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-sm font-semibold text-slate-900">配下代理店</div>
            <div className="mt-3 grid gap-2 text-sm">
              {subordinateAgencies.map((agency) => (
                <div key={agency.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="font-medium text-slate-900">{agency.name}</div>
                  <div className="text-xs text-slate-500">{agency.email}</div>
                  <div className="mt-1 text-xs text-slate-600">
                    直属コネクター: {connectorsForAgency(db, agency.id).length}人
                  </div>
                </div>
              ))}
              {subordinateAgencies.length === 0 ? <div className="text-sm text-slate-500">配下代理店はいません。</div> : null}
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
